import { useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient.js";

// ── Type Interfaces ──────────────────────────────────

interface QueuedEvent {
  event_type: string;
  event_category: string;
  metadata: Record<string, unknown>;
  page: string;
  created_at: string;
}

export interface UseEventTrackerReturn {
  trackEvent: (
    eventType: string,
    eventCategory: string,
    metadata?: Record<string, unknown>,
    page?: string | null
  ) => void;
  flushEvents: () => Promise<void>;
  sessionId: string | null;
}

/**
 * Event tracker hook — accepts clientId as parameter because it is
 * called inside ClientLayout *before* <ClientProvider> wraps the tree.
 * NOTE: We resolve the auth UUID internally since portal_events.user_id is UUID,
 * not the text-based client_id code.
 */
const useEventTracker = (clientId: string | undefined): UseEventTrackerReturn => {
  const authUserIdRef = useRef<string | null>(null);

  // Resolve auth UUID once on mount
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) authUserIdRef.current = data.user.id;
    });
  }, []);
  const eventQueueRef = useRef<QueuedEvent[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // Initialize session ID on mount
  useEffect(() => {
    if (!sessionIdRef.current) {
      const storedSessionId = sessionStorage.getItem("sessionId");
      if (storedSessionId) {
        sessionIdRef.current = storedSessionId;
      } else {
        sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("sessionId", sessionIdRef.current);
      }
    }
  }, []);

  // Flush events to database (batched every 5 seconds or on explicit call)
  const flushEvents = useCallback(async (): Promise<void> => {
    const userId = authUserIdRef.current;
    if (eventQueueRef.current.length === 0 || !clientId || !userId) {
      return;
    }

    const eventsToFlush = [...eventQueueRef.current];
    eventQueueRef.current = [];

    try {
      // Get user's IP address and user agent
      const ipAddress: string =
        (await fetch("https://api.ipify.org?format=json")
          .then((r) => r.json())
          .then((d: { ip: string }) => d.ip)
          .catch(() => "unknown")) || "unknown";

      const userAgent: string = navigator.userAgent;

      const enrichedEvents = eventsToFlush.map((event) => ({
        user_id: userId,
        event_type: event.event_type,
        event_category: event.event_category,
        metadata: event.metadata || {},
        page: event.page || window.location.pathname,
        ip_address: ipAddress,
        user_agent: userAgent,
        session_id: sessionIdRef.current,
        created_at: event.created_at || new Date().toISOString(),
      }));

      const { error } = await supabase.from("portal_events").insert(enrichedEvents);

      if (error) {
        console.error("Failed to log events:", error);
        // Re-queue failed events for retry (simple approach)
        eventQueueRef.current = [
          ...enrichedEvents.map((e) => ({
            event_type: e.event_type,
            event_category: e.event_category,
            metadata: e.metadata as Record<string, unknown>,
            page: e.page,
            created_at: e.created_at,
          })),
          ...eventQueueRef.current,
        ];
      }
    } catch (error) {
      console.error("Error flushing events:", error);
    }
  }, [clientId]);

  // Schedule flush every 5 seconds
  useEffect(() => {
    const interval = setInterval(flushEvents, 5000);
    return () => {
      clearInterval(interval);
      // Flush remaining events on unmount
      flushEvents();
    };
  }, [flushEvents]);

  // Queue an event for batching
  const trackEvent = useCallback(
    (
      eventType: string,
      eventCategory: string,
      metadata: Record<string, unknown> = {},
      page: string | null = null
    ): void => {
      if (!clientId) return;

      eventQueueRef.current.push({
        event_type: eventType,
        event_category: eventCategory,
        metadata,
        page: page || window.location.pathname,
        created_at: new Date().toISOString(),
      });

      // If queue is large, flush immediately
      if (eventQueueRef.current.length >= 10) {
        if (flushTimeoutRef.current) {
          clearTimeout(flushTimeoutRef.current);
        }
        flushEvents();
      } else {
        // Otherwise, schedule a flush
        if (flushTimeoutRef.current) {
          clearTimeout(flushTimeoutRef.current);
        }
        flushTimeoutRef.current = setTimeout(flushEvents, 2000);
      }
    },
    [clientId, flushEvents]
  );

  return {
    trackEvent,
    flushEvents,
    sessionId: sessionIdRef.current,
  };
};

export default useEventTracker;
